<template>
  <div id="diagram">
    <svg v-bind:width="width" v-bind:height="height" ref="svg"></svg>
  </div>
</template>

<script>
import numeral from "numeral-es6";

import { select } from "d3-selection";
import { event as d3event } from "d3-selection";
import { symbol, symbolTriangle, symbolSquare } from "d3-shape";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter
} from "d3-force";
import { drag } from "d3-drag";
import { zoom, zoomIdentity, zoomTransform } from "d3-zoom";

import MPCStore from "../stores/MPCStore";
import OPFStore from "../stores/OPFStore";
import PFStore from "../stores/PFStore";
import ConfigStore from "../stores/ConfigStore";

import {
  BUS_I,
  PD,
  QD,
  GS,
  BS,
  VM,
  VA,
  GEN_BUS,
  PG,
  QG,
  F_BUS,
  T_BUS,
  TAP,
  PF,
  QF,
  PT,
  QT,
  DCLINE_F_BUS,
  DCLINE_T_BUS
} from "../stores/idx";

export default {
  name: "Diagram",
  data() {
    return {
      width: 0,
      height: 0,
      ConfigStore: ConfigStore,
      PFStore: PFStore,
      OPFStore: OPFStore
    };
  },
  mounted() {
    const component = this;

    this.$watch("ConfigStore.data.config.graphView", component.draw);
    this.$watch("ConfigStore.data.config.branchFlows", component.draw);
    this.$watch("PFStore.data.pf", () => {
      const view = ConfigStore.data.config.graphView;
      if (view === "pf") component.draw();
    });
    this.$watch("OPFStore.data.opf", () => {
      const view = ConfigStore.data.config.graphView;
      if (view === "opf") component.draw();
    });

    this.$watch(
      "ConfigStore.data.config.busNumbers",
      show => {
        let svg = select(component.$refs["svg"]);
        let labels = svg.selectAll("g.nodes text.bus");
        labels.classed("hidden", !show);
      },
      { deep: true }
    );

    window.addEventListener("resize", this.onResize);
    this.onResize();
    this.draw();
  },
  methods: {
    onResize() {
      this.width = this.$el.offsetWidth;
      this.height = this.$el.offsetHeight;
    },
    draw() {
      let view = ConfigStore.data.config.graphView;
      let branchFlows = ConfigStore.data.config.branchFlows;
      if (!view) view = "initial";
      if (!branchFlows) branchFlows = "active";

      let buses, gens, branches, dclines;
      let flows = false;
      switch (view) {
        case "initial":
          buses = MPCStore.methods.getBuses();
          gens = MPCStore.methods.getGens();
          branches = MPCStore.methods.getBranches();
          dclines = MPCStore.methods.getDCLines();
          break;
        case "pf":
          buses = PFStore.methods.getBuses();
          gens = PFStore.methods.getGens();
          branches = PFStore.methods.getBranches();
          dclines = PFStore.methods.getDCLines();
          flows = true;
          break;
        case "opf":
          buses = OPFStore.methods.getBuses();
          gens = OPFStore.methods.getGens();
          branches = OPFStore.methods.getBranches();
          dclines = OPFStore.methods.getDCLines();
          flows = true;
          break;
        default:
          throw view;
      }

      let busNodes = buses.map((bus, i) => ({
        idx: i,
        id: "" + bus[BUS_I],
        label: busLabel(bus)
      }));
      let loadNodes = buses.filter(isLoad).map(bus => ({
        bus_i: bus[BUS_I],
        id: "l" + bus[BUS_I],
        label: loadLabel(bus)
      }));
      let loadConns = buses.filter(isLoad).map(bus => ({
        bus_i: bus[BUS_I],
        source: "" + bus[BUS_I],
        target: "l" + bus[BUS_I]
      }));
      let shuntNodes = buses.filter(isShunt).map(bus => ({
        bus_i: bus[BUS_I],
        id: "s" + bus[BUS_I]
      }));
      let shuntConns = buses.filter(isShunt).map(bus => ({
        bus_i: bus[BUS_I],
        source: "" + bus[BUS_I],
        target: "s" + bus[BUS_I]
      }));

      let genNodes = gens.map((g, i) => ({
        idx: i,
        id: "g" + (i + 1),
        label: genLabel(g)
      }));
      let genConns = gens.map((gen, i) => ({
        idx: i,
        source: "g" + (i + 1),
        target: "" + gen[GEN_BUS]
      }));

      let branchLinks = branches.map((br, i) => {
        let l = {
          idx: i,
          source: "" + br[F_BUS],
          target: "" + br[T_BUS],
          tfmr: br[TAP] !== 0
        };
        if (flows) {
          l.fromLabel = fromLabel(br);
          l.toLabel = toLabel(br);
          l.fromFlow = branchFlows === "reactive" ? br[QF] : br[PF];
          l.toFlow = branchFlows === "reactive" ? br[QT] : br[PT];
        }
        return l;
      });
      let lineLinks = branchLinks.filter(d => !d.tfmr);
      let tfmrLinks = branchLinks.filter(d => d.tfmr);

      let dclineLinks = dclines.map((ln, i) => ({
        idx: i,
        source: "" + ln[DCLINE_F_BUS],
        target: "" + ln[DCLINE_T_BUS]
      }));

      let positions = ConfigStore.methods.getPositions();
      if (positions) {
        [...busNodes, ...loadNodes, ...shuntNodes, ...genNodes].forEach(nd => {
          if (positions.hasOwnProperty(nd.id)) {
            nd["x"] = positions[nd.id].x;
            nd["y"] = positions[nd.id].y;
          }
        });
      }

      let svg = select(this.$refs["svg"]);
      svg.selectAll("g").remove();

      let linkGroup = svg.append("g").attr("class", "links");
      linkGroup
        .selectAll("line.load")
        .data(loadConns)
        .join("line")
        .attr("class", "load conn");
      linkGroup
        .selectAll("line.shunt")
        .data(shuntConns)
        .join("line")
        .attr("class", "shunt conn");
      linkGroup
        .selectAll("line.gen")
        .data(genConns)
        .join("line")
        .attr("class", "gen conn");
      linkGroup
        .selectAll("line.line")
        .data(lineLinks)
        .join("line")
        .attr("class", "branch line");
      linkGroup
        .selectAll("line.tfmr")
        .data(tfmrLinks)
        .join("line")
        .attr("class", "branch tfmr");
      linkGroup
        .selectAll("circle.tfmr")
        .data(tfmrLinks)
        .join("circle")
        .attr("r", 6)
        .attr("class", "branch tfmr");
      linkGroup
        .selectAll("line.dcline")
        .data(dclineLinks)
        .join("line")
        .attr("class", "dcline");
      let allLinks = linkGroup.selectAll("line");
      let tfmrNodes = linkGroup.selectAll("circle.tfmr");

      let loadSymbol = symbol()
        .type(symbolTriangle)
        .size(100);
      let shuntSymbol = symbol()
        .type(symbolSquare)
        .size(100);

      let nodeGroup = svg.append("g").attr("class", "nodes");
      nodeGroup
        .selectAll("rect.bus")
        .data(busNodes)
        .enter()
        .append("rect")
        .attr("class", "bus")
        .attr("x", -9)
        .attr("y", -3)
        .attr("width", 18)
        .attr("height", 6)
        .on("mouseover", mouseoverNode("bus"))
        .on("mouseout", mouseoutNode("bus"));
      nodeGroup
        .selectAll("text.bus")
        .data(busNodes)
        .enter()
        .append("text")
        .attr("class", "bus")
        .classed("hidden", !ConfigStore.data.config.busNumbers)
        .attr("dominant-baseline", "central")
        .attr("text-anchor", "middle")
        .text(d => d.id)
        .on("mouseover", mouseoverNode("bus"))
        .on("mouseout", mouseoutNode("bus"));
      nodeGroup
        .selectAll("path.load")
        .data(loadNodes)
        .join("path")
        .attr("class", "load")
        .attr("d", loadSymbol)
        .on("mouseover", mouseoverNode("load"))
        .on("mouseout", mouseoutNode("load"));
      nodeGroup
        .selectAll("path.shunt")
        .data(shuntNodes)
        .join("path")
        .attr("class", "shunt")
        .attr("d", shuntSymbol);
      nodeGroup
        .selectAll("circle.gen")
        .data(genNodes)
        .join("circle")
        .attr("class", "gen")
        .attr("r", 6)
        .on("mouseover", mouseoverNode("gen"))
        .on("mouseout", mouseoutNode("gen"));
      let allNodes = nodeGroup.selectAll("*").call(
        drag()
          .on("start", dragStarted)
          .on("drag", dragged)
          .on("end", dragEnded)
      );

      let nodeLabelGroup = svg.append("g").attr("class", "node labels");
      nodeLabelGroup
        .selectAll("text.bus")
        .data(busNodes)
        .join("text")
        .attr("class", "bus")
        .text(d => d.label)
        .on("mouseover", mouseoverNode("bus"))
        .on("mouseout", mouseoutNode("bus"));
      nodeLabelGroup
        .selectAll("text.load")
        .data(loadNodes)
        .join("text")
        .attr("class", "load")
        .text(d => d.label)
        .on("mouseover", mouseoverNode("load"))
        .on("mouseout", mouseoutNode("load"));
      nodeLabelGroup
        .selectAll("text.gen")
        .data(genNodes)
        .join("text")
        .attr("class", "gen")
        .text(d => d.label)
        .on("mouseover", mouseoverNode("gen"))
        .on("mouseout", mouseoutNode("gen"));
      let nodeLabels = nodeLabelGroup.selectAll("*");

      if (flows) {
        const sizeMax = 50;
        const flowSymbol = symbol().type(symbolTriangle);

        let maxFrom = Math.max(
          ...branches.map(br => {
            return Math.abs(branchFlows === "reactive" ? br[QF] : br[PF]);
          })
        );
        let maxTo = Math.max(
          ...branches.map(br => {
            return Math.abs(branchFlows === "reactive" ? br[QT] : br[PT]);
          })
        );
        let maxFlow = Math.max(maxFrom, maxTo);
        let flowSymbolSize = flow => {
          return sizeMax * (Math.abs(flow) / maxFlow);
        };

        var flowGroup = svg.append("g").attr("class", "flows");
        flowGroup
          .selectAll("path.line.from")
          .data(lineLinks)
          .join("path")
          .attr("class", "branch line from " + branchFlows)
          .attr("d", d => flowSymbol.size(flowSymbolSize(d.fromFlow))(d))
          .on("mouseover", mouseoverLink("from"))
          .on("mouseout", mouseoutLink("from"));
        flowGroup
          .selectAll("path.line.to")
          .data(lineLinks)
          .join("path")
          .attr("class", "branch line to " + branchFlows)
          .attr("d", d => flowSymbol.size(flowSymbolSize(d.toFlow))(d))
          .on("mouseover", mouseoverLink("to"))
          .on("mouseout", mouseoutLink("to"));
        flowGroup
          .selectAll("path.tfmr.from")
          .data(tfmrLinks)
          .join("path")
          .attr("class", "branch tfmr from " + branchFlows)
          .attr("d", d => flowSymbol.size(flowSymbolSize(d.fromFlow))(d))
          .on("mouseover", mouseoverLink("from"))
          .on("mouseout", mouseoutLink("from"));
        flowGroup
          .selectAll("path.tfmr.to")
          .data(tfmrLinks)
          .join("path")
          .attr("class", "branch tfmr to " + branchFlows)
          .attr("d", d => flowSymbol.size(flowSymbolSize(d.toFlow))(d))
          .on("mouseover", mouseoverLink("to"))
          .on("mouseout", mouseoutLink("to"));
        var fromFlows = flowGroup.selectAll(".from");
        var toFlows = flowGroup.selectAll(".to");

        var linkLabelGroup = svg.append("g").attr("class", "link labels");
        linkLabelGroup
          .selectAll("text.line.from")
          .data(lineLinks)
          .join("text")
          .attr("class", "line from")
          .text(d => d.fromLabel)
          .on("mouseover", mouseoverLink("from"))
          .on("mouseout", mouseoutLink("from"));
        linkLabelGroup
          .selectAll("text.line.to")
          .data(lineLinks)
          .join("text")
          .attr("class", "line to")
          .text(d => d.toLabel)
          .on("mouseover", mouseoverLink("to"))
          .on("mouseout", mouseoutLink("to"));
        linkLabelGroup
          .selectAll("text.tfmr.from")
          .data(tfmrLinks)
          .join("text")
          .attr("class", "tfmr from")
          .text(d => d.fromLabel)
          .on("mouseover", mouseoverLink("from"))
          .on("mouseout", mouseoutLink("from"));
        linkLabelGroup
          .selectAll("text.tfmr.to")
          .data(tfmrLinks)
          .join("text")
          .attr("class", "tfmr to")
          .text(d => d.toLabel)
          .on("mouseover", mouseoverLink("to"))
          .on("mouseout", mouseoutLink("to"));
        var fromLabels = linkLabelGroup.selectAll(".from");
        var toLabels = linkLabelGroup.selectAll(".to");
      }

      let simulation = forceSimulation()
        .nodes([...busNodes, ...loadNodes, ...shuntNodes, ...genNodes])
        .force(
          "link",
          forceLink()
            .id(d => d.id)
            .links([...lineLinks, ...tfmrLinks, ...dclineLinks])
            .distance(lnk => (lnk.tfmr ? 10 : 100))
        )
        .force(
          "conn",
          forceLink()
            .id(d => d.id)
            .links([...loadConns, ...shuntConns, ...genConns])
        )
        .force("charge", forceManyBody())
        .force("center", forceCenter(this.width / 2, this.height / 2))
        .alphaMin(0.01)
        .on("tick.node", updateNodePositions)
        .on("tick.link", updateLinkPositions)
        .on("end.save", savePositions);
      if (Object.keys(positions).length !== 0) {
        simulation.alpha(0.01);
      } else {
        let decay = simulation.alphaDecay();
        simulation
          .stop()
          .alphaDecay(0)
          .tick(500)
          .alphaDecay(decay)
          .restart();
      }

      let zoomer = zoom().on("zoom", function() {
        let transform = d3event.transform;

        linkGroup.attr("transform", transform);
        nodeGroup.attr("transform", transform);

        nodeLabelGroup.attr("transform", transform);
        if (flows) {
          flowGroup.attr("transform", transform);
          linkLabelGroup.attr("transform", transform);
        }

        ConfigStore.methods.setTransform(transform);
      });
      svg.call(zoomer);

      let transform = ConfigStore.methods.getTransform();
      if (transform) {
        let t = zoomIdentity
          .translate(transform.x, transform.y)
          .scale(transform.k);
        zoomer.transform(svg, t);
      }

      let router = this.$router;
      nodeGroup.selectAll(".bus").on("click.nav", function(d) {
        router.push({ name: "bus", params: { ord: d.idx + 1 } });
      });
      nodeGroup.selectAll(".load").on("click.nav", function(d) {
        let idx = findBusIdx(d.bus_i);
        router.push({ name: "bus", params: { ord: idx + 1 } });
      });
      nodeGroup.selectAll(".shunt").on("click.nav", function(d) {
        let idx = findBusIdx(d.bus_i);
        router.push({ name: "bus", params: { ord: idx + 1 } });
      });
      nodeGroup.selectAll(".gen").on("click.nav", function(d) {
        router.push({ name: "gen", params: { ord: d.idx + 1 } });
      });

      linkGroup.selectAll(".load").on("click.nav", function(d) {
        let idx = findBusIdx(d.bus_i);
        router.push({ name: "bus", params: { ord: idx + 1 } });
      });
      linkGroup.selectAll(".shunt").on("click.nav", function(d) {
        let idx = findBusIdx(d.bus_i);
        router.push({ name: "bus", params: { ord: idx + 1 } });
      });
      linkGroup.selectAll(".gen").on("click.nav", function(d) {
        router.push({ name: "gen", params: { ord: d.idx + 1 } });
      });
      linkGroup.selectAll(".branch").on("click.nav", function(d) {
        router.push({ name: "branch", params: { ord: d.idx + 1 } });
      });
      linkGroup.selectAll(".dcline").on("click.nav", function(d) {
        router.push({ name: "dcline", params: { ord: d.idx + 1 } });
      });

      function updateNodePositions() {
        allNodes.attr("transform", d => {
          let t = `translate(${d.x},${d.y})`;
          if (d.id[0] === "l") t += " rotate(180)";
          return t;
        });
        nodeLabels.attr("transform", d => `translate(${d.x},${d.y})`);
      }
      function updateLinkPositions() {
        allLinks
          .attr("x1", d => d.source.x)
          .attr("y1", d => d.source.y)
          .attr("x2", d => d.target.x)
          .attr("y2", d => d.target.y);

        tfmrNodes.attr("transform", d => {
          let x = (d.source.x + d.target.x) / 2;
          let y = (d.source.y + d.target.y) / 2;
          return `translate(${x},${y})`;
        });

        if (flows) {
          fromFlows.attr("transform", d => {
            let m = midpoint(d.source, d.target);
            m = midpoint(d.source, m);
            let ang = lineAngle(d.source, d.target);
            ang += d.fromFlow < 0 ? -90 : 90;
            return `translate(${m.x},${m.y}) rotate(${ang})`;
          });
          toFlows.attr("transform", d => {
            let m = midpoint(d.source, d.target);
            m = midpoint(m, d.target);
            let ang = lineAngle(d.source, d.target);
            ang += d.toFlow < 0 ? 90 : -90;
            return `translate(${m.x},${m.y}) rotate(${ang})`;
          });

          fromLabels.attr("transform", d => {
            let m = midpoint(d.source, d.target);
            m = midpoint(d.source, m);
            return `translate(${m.x},${m.y})`;
          });
          toLabels.attr("transform", d => {
            let m = midpoint(d.source, d.target);
            m = midpoint(m, d.target);
            return `translate(${m.x},${m.y})`;
          });
        }
      }
      function savePositions() {
        let positions = {};
        allNodes.each(function(d) {
          positions[d.id] = { x: d.x, y: d.y };
        });
        ConfigStore.methods.setPositions(positions);
      }

      function dragStarted(d) {
        if (!d3event.active) {
          simulation.alphaTarget(0.3).restart();
        }
        d.fx = d.x;
        d.fy = d.y;
      }

      function dragged(d) {
        d.fx = d3event.x;
        d.fy = d3event.y;
      }

      function dragEnded(d) {
        if (!d3event.active) {
          simulation.alphaTarget(0);
        }
        d.fx = null;
        d.fy = null;
      }

      function mouseoverNode(cls) {
        return () => {
          let deviceLabels = ConfigStore.data.config.deviceLabels;
          let k = zoomTransform(svg.node()).k;
          if (deviceLabels && k >= 0.7) {
            nodeLabelGroup.selectAll(`text.${cls}`).classed("selected", true);
          }
        };
      }
      function mouseoutNode(cls) {
        return () => {
          nodeLabelGroup.selectAll(`text.${cls}`).classed("selected", false);
        };
      }
      function mouseoverLink(cls) {
        return () => {
          let deviceLabels = ConfigStore.data.config.deviceLabels;
          let k = zoomTransform(svg.node()).k;
          if (deviceLabels && k >= 0.7) {
            linkLabelGroup.selectAll(`text.${cls}`).classed("selected", true);
          }
        };
      }
      function mouseoutLink(cls) {
        return () => {
          linkLabelGroup.selectAll(`text.${cls}`).classed("selected", false);
        };
      }

      function isLoad(bus) {
        return bus[PD] !== 0 || bus[QD] !== 0;
      }
      function isShunt(bus) {
        return bus[GS] !== 0 || bus[BS] !== 0;
      }

      function findBusIdx(n) {
        switch (view) {
          case "initial":
            return MPCStore.methods.findBusIdx(n);
          case "pf":
            return PFStore.methods.findBusIdx(n);
          case "opf":
            return OPFStore.methods.findBusIdx(n);
          default:
            throw view;
        }
      }

      function busLabel(bus) {
        let vm = numeral(bus[VM]).format("0.[00]");
        let va = numeral(bus[VA]).format("0.[0]");
        return `${vm}pu∠${va}°`;
      }
      function loadLabel(bus) {
        let pd = numeral(bus[PD]).format("0.[00]");
        let qd = numeral(bus[QD]).format("0.[00]");
        return `${pd}${qd < 0 ? "" : "+"}${qd}i MVA`;
      }
      function genLabel(gen) {
        let pg = numeral(gen[PG]).format("0.[00]");
        let qg = numeral(gen[QG]).format("0.[00]");
        return `${pg}${qg < 0 ? "" : "+"}${qg}i MVA`;
      }
      function fromLabel(br) {
        switch (branchFlows) {
          case "active":
            return numeral(Math.abs(br[PF])).format("0.[00]") + " MW";
          case "reactive":
            return numeral(Math.abs(br[QF])).format("0.[00]") + " MVAr";
          default:
            return "";
        }
      }
      function toLabel(br) {
        switch (branchFlows) {
          case "active":
            return numeral(Math.abs(br[PT])).format("0.[00]") + " MW";
          case "reactive":
            return numeral(Math.abs(br[QT])).format("0.[00]") + " MVAr";
          default:
            return "";
        }
      }

      function midpoint(p1, p2) {
        let x = (p1.x + p2.x) / 2;
        let y = (p1.y + p2.y) / 2;
        return { x, y };
      }
      function lineAngle(p1, p2) {
        return Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
      }
    }
  }
};
</script>

<style>
#diagram {
  flex: 1;
}

.links line.line {
  stroke-width: 3;
  stroke: #999;
  stroke-opacity: 0.9;
}

.links line.tfmr {
  stroke-width: 4;
  stroke: #aa00ff;
  stroke-opacity: 0.9;
}

.links circle.tfmr {
  fill: #aa00ff;
  stroke: #8f00d7;
  stroke-width: 2px;
}

.links line.dcline {
  stroke-width: 4;
  stroke: #222222;
  stroke-opacity: 0.9;
}

.links line.conn {
  stroke-width: 2;
  stroke: #666;
  stroke-opacity: 0.6;
}

.nodes rect.bus {
  fill: #2979ff;
  stroke: #2962ff;
  stroke-width: 2px;
}

.nodes text.bus {
  font-size: 0.3rem;
  fill: #fff;

  visibility: visible;
  opacity: 1;

  text-shadow: -1px -1px 0 #222, 0 -1px 0 #222, 1px -1px 0 #222, 1px 0 0 #222,
    1px 1px 0 #222, 0 1px 0 #222, -1px 1px 0 #222, -1px 0 0 #222;
}

.nodes text.bus.hidden {
  visibility: hidden;
  opacity: 0;
}

.nodes path.load {
  fill: #ffea00;
  stroke: #ffd600;
  stroke-width: 2px;
}

.nodes path.shunt {
  fill: #00e676;
  stroke: #00c853;
  stroke-width: 2px;
}

.nodes circle.gen {
  fill: #ff1744;
  stroke: #d50000;
  stroke-width: 2px;
}

.node.labels text {
  font-size: 0.6rem;

  visibility: hidden;
  opacity: 0;
  transition: visibility 0.3s linear, opacity 0.3s linear;

  text-shadow: -1px -1px 0 #fff, 0 -1px 0 #fff, 1px -1px 0 #fff, 1px 0 0 #fff,
    1px 1px 0 #fff, 0 1px 0 #fff, -1px 1px 0 #fff, -1px 0 0 #fff;
}

.node.labels text.selected {
  visibility: visible;
  opacity: 1;
}

.link.labels text {
  font-size: 0.6rem;

  visibility: hidden;
  opacity: 0;
  transition: visibility 0.3s linear, opacity 0.3s linear;

  text-shadow: -1px -1px 0 #fff, 0 -1px 0 #fff, 1px -1px 0 #fff, 1px 0 0 #fff,
    1px 1px 0 #fff, 0 1px 0 #fff, -1px 1px 0 #fff, -1px 0 0 #fff;
}

.link.labels text.selected {
  visibility: visible;
  opacity: 1;
}

.flows path.line.active,
.flows path.tfmr.active {
  fill: #ff1744;
}

.flows path.line.reactive,
.flows path.tfmr.reactive {
  fill: #2962ff;
}
</style>
